import React, { createContext, useContext, useEffect, useState } from 'react';
import { MIN_QTY } from '../lib/config';

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem('es_cart');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('es_cart', JSON.stringify(items));
  }, [items]);

  const getUnitPrice = (product, qty) => {
    if (product.tier2 && qty >= product.tier2.qty) return product.tier2.price;
    if (product.tier1 && qty >= product.tier1.qty) return product.tier1.price;
    return product.price;
  };

  const enforceMin = (q) => Math.max(MIN_QTY, parseInt(q) || MIN_QTY);

  const addItem = (product, qty = MIN_QTY) => {
    const addQty = Math.max(MIN_QTY, qty);
    setItems((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.id === product.id ? { ...i, qty: enforceMin(i.qty + addQty) } : i
        );
      }
      return [
        ...prev,
        {
          id: product.id,
          name: product.name,
          image: product.image,
          price: product.price,
          mrp: product.mrp,
          tier1: product.tier1,
          tier2: product.tier2,
          qty: addQty,
        },
      ];
    });
  };

  const updateQty = (id, qty) => {
    if (qty <= 0) return removeItem(id);
    const clamped = enforceMin(qty);
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, qty: clamped } : i)));
  };

  const removeItem = (id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const clearCart = () => setItems([]);

  const subtotal = items.reduce((sum, i) => sum + getUnitPrice(i, i.qty) * i.qty, 0);
  const itemCount = items.reduce((sum, i) => sum + i.qty, 0);
  const gst = subtotal * 0.18;
  const total = subtotal + gst;

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        updateQty,
        removeItem,
        clearCart,
        subtotal,
        gst,
        total,
        itemCount,
        getUnitPrice,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
